import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { getPlanner, createPlanner, updatePlanner, getClients } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Combobox } from '../components/ui/combobox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  ArrowLeft, Save, Loader2, Target, TrendingUp, Funnel as FunnelIcon, Megaphone,
  Users, Receipt, BarChart3, Plus, Trash2, AlertTriangle, ChevronDown,
  CircleDollarSign, ArrowDownRight, CheckCircle2, XCircle, Calendar, Download
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ComposedChart, Line
} from 'recharts';

const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const formatNumber = (v) => new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(v || 0);
const formatPct = (v) => `${(v || 0).toFixed(1)}%`;

const DEFAULT_DATA = {
  objectives: { revenue_target_y1: 0, revenue_target_y2: 0, revenue_target_y3: 0 },
  revenue: { avg_ticket: 0, margin_pct: 0, referral_pct: 0, upsell_pct: 0, upsell_avg_ticket: 0, upsell_margin_pct: 0, upsell_cycle_months: 0, upsell_in_target: true, recurring_pct: 0, recurring_annual_ticket: 0, recurring_margin_pct: 0, churn_rate: 0, recurring_base: 'new' },
  funnel: { appointment_rate: 0, show_up_rate: 0, conversion_rate: 0, sales_cycle_months: 0, lead_target_pct: 90, click_to_visit_pct: 80, optin_conversion_pct: 10, ctr_pct: 1 },
  marketing: { cac_target: 0 },
  team: { setter_appointments_per_month: 40, setter_fee_per_appointment: 50, setter_fixed_salary: 0, closer_appointments_per_month: 60, closer_commission_fixed: 0, closer_commission_pct: 0, closer_upsell_commission_fixed: 0, closer_upsell_commission_pct: 0, closer_recurring_commission_fixed: 0, closer_recurring_commission_pct: 0, closer_fixed_salary: 0 },
  costs: { fixed_costs: [{ name: 'Agency', amount: 5000, type: 'monthly', is_percentage: false }, { name: 'Fee Ads', amount: 15, type: 'monthly', is_percentage: true }, { name: 'CRM e Tools', amount: 500, type: 'monthly', is_percentage: false }], custom_costs: [] },
  maximization: { db_appointment_rate: 0, db_show_up_rate: 50, mx_fee_per_appointment: 100 },
};

function NumInput({ label, value, onChange, suffix, prefix, min, step, className, hint }) {
  const [focused, setFocused] = useState(false);
  const [rawInput, setRawInput] = useState('');

  const formatWithDots = (num) => {
    if (!num && num !== 0) return '';
    if (num === 0) return '';
    return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(num);
  };

  const displayValue = focused ? rawInput : formatWithDots(value);

  const handleFocus = () => {
    setFocused(true);
    setRawInput(value ? String(value) : '');
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    setRawInput(raw);
    const cleaned = raw.replace(/\./g, '').replace(',', '.');
    if (cleaned === '' || cleaned === '-') { onChange(0); return; }
    const num = parseFloat(cleaned);
    if (!isNaN(num)) onChange(num);
  };

  return (
    <div className={`space-y-1 ${className || ''}`}>
      {label && <Label className="text-sm font-medium text-gray-700">{label}</Label>}
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>}
        <Input
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`${prefix ? 'pl-8' : ''} ${suffix ? 'pr-10' : ''}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

function CostAmountInput({ value, onChange, placeholder }) {
  const [focused, setFocused] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const fmt = (n) => (!n ? '' : new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(n));
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={focused ? rawInput : fmt(value)}
      onChange={(e) => { const r = e.target.value; setRawInput(r); const c = r.replace(/\./g, '').replace(',', '.'); const n = parseFloat(c); onChange(isNaN(n) ? 0 : n); }}
      onFocus={() => { setFocused(true); setRawInput(value ? String(value) : ''); }}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
    />
  );
}

function WarningBadge({ level, children }) {
  const styles = {
    danger: 'bg-red-100 text-red-700 border-red-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    success: 'bg-green-100 text-green-700 border-green-200',
  };
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${styles[level] || styles.warning}`}>
      {level === 'danger' && <XCircle className="w-4 h-4 shrink-0" />}
      {level === 'warning' && <AlertTriangle className="w-4 h-4 shrink-0" />}
      {level === 'success' && <CheckCircle2 className="w-4 h-4 shrink-0" />}
      <span>{children}</span>
    </div>
  );
}

function KPICard({ title, value, subtitle, icon: Icon, color }) {
  const bg = { orange: 'bg-orange-50 text-orange-700', blue: 'bg-blue-50 text-blue-700', green: 'bg-green-50 text-green-700', purple: 'bg-purple-50 text-purple-700', red: 'bg-red-50 text-red-700', amber: 'bg-amber-50 text-amber-700' };
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</span>
          {Icon && <div className={`w-7 h-7 rounded-md flex items-center justify-center ${bg[color] || bg.orange}`}><Icon className="w-3.5 h-3.5" /></div>}
        </div>
        <div className="text-xl font-bold text-gray-900">{value}</div>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

export default function PlannerEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNew = !id;

  const [name, setName] = useState('Nuovo Planner');
  const [clientId, setClientId] = useState('');
  const [data, setData] = useState(JSON.parse(JSON.stringify(DEFAULT_DATA)));
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [selectedYear, setSelectedYear] = useState(1);
  const [timeView, setTimeView] = useState('annual');
  const [showAdvancedFunnel, setShowAdvancedFunnel] = useState(false);
  const [activeTab, setActiveTab] = useState('ricavi');
  const { hasRole } = useAuth();
  const isReadOnly = !hasRole(['admin', 'project_manager']);

  useEffect(() => {
    loadClients();
    if (!isNew) loadPlanner();
  }, [id]);

  const loadClients = async () => {
    try {
      const res = await getClients();
      setClients(res.data.data || res.data);
    } catch {}
  };

  const loadPlanner = async () => {
    try {
      const res = await getPlanner(id);
      const p = res.data;
      setName(p.name || 'Senza nome');
      setClientId(p.client_id || '');
      if (p.data && Object.keys(p.data).length > 0) {
        setData(prev => {
          const merged = JSON.parse(JSON.stringify(DEFAULT_DATA));
          Object.keys(p.data).forEach(section => {
            if (merged[section]) {
              if (section === 'costs') {
                merged.costs.fixed_costs = p.data.costs?.fixed_costs || merged.costs.fixed_costs;
                merged.costs.custom_costs = p.data.costs?.custom_costs || [];
              } else {
                Object.keys(p.data[section]).forEach(key => {
                  if (section === 'team' && key === 'closer_sales_per_month' && !p.data.team.closer_appointments_per_month) {
                    merged.team.closer_appointments_per_month = p.data.team.closer_sales_per_month;
                  } else if (key in merged[section]) {
                    merged[section][key] = p.data[section][key];
                  }
                });
              }
            }
          });
          return merged;
        });
      }
    } catch (err) {
      toast({ title: 'Errore', description: 'Planner non trovato', variant: 'destructive' });
      navigate('/toolkit');
    } finally {
      setLoading(false);
    }
  };

  // Auto-create planner immediately for "new" route
  const autoCreated = useRef(false);
  useEffect(() => {
    if (isNew && !autoCreated.current) {
      autoCreated.current = true;
      const doCreate = async () => {
        try {
          const res = await createPlanner({ name: 'Nuovo Planner', client_id: null, data });
          navigate(`/toolkit/planner/${res.data.id}`, { replace: true });
        } catch (err) {
          toast({ title: 'Errore', description: 'Impossibile creare il planner', variant: 'destructive' });
          setLoading(false);
        }
      };
      doCreate();
    } else if (!isNew) {
      loadPlanner();
    }
  }, [id]);

  const updateSection = useCallback((section, field, value) => {
    setData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  }, []);

  const handleSave = async () => {
    if (!name.trim()) { toast({ title: 'Inserisci un nome per il planner', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await createPlanner({ name, client_id: clientId || null, data });
        toast({ title: 'Planner creato' });
        navigate(`/toolkit/planner/${res.data.id}`, { replace: true });
      } else {
        await updatePlanner(id, { name, client_id: clientId || null, data });
      }
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile salvare', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Auto-save with debounce (only for existing planners)
  const autoSaveTimer = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const W = pdf.internal.pageSize.getWidth();
      const H = pdf.internal.pageSize.getHeight();
      const M = 14; // margin
      const cW = W - M * 2; // content width
      let y = M;

      const addPage = () => { pdf.addPage(); y = M; };
      const checkPage = (need) => { if (y + need > H - M) addPage(); };

      // --- Colors ---
      const COL = {
        title: [30, 30, 30],
        subtitle: [100, 100, 100],
        text: [55, 65, 81],
        muted: [120, 130, 140],
        accent: [234, 88, 12], // orange
        green: [5, 150, 105],
        red: [220, 38, 38],
        blue: [37, 99, 235],
        indigo: [79, 70, 229],
        purple: [124, 58, 237],
        amber: [217, 119, 6],
        divider: [229, 231, 235],
        cardBg: [249, 250, 251],
        white: [255, 255, 255],
      };

      const setCol = (c) => { pdf.setTextColor(c[0], c[1], c[2]); };
      const setFill = (c) => { pdf.setFillColor(c[0], c[1], c[2]); };
      const setDraw = (c) => { pdf.setDrawColor(c[0], c[1], c[2]); };

      // --- Helper: section title with colored bar ---
      const sectionTitle = (text, barColor) => {
        checkPage(12);
        setFill(barColor || COL.accent);
        pdf.rect(M, y, 1.5, 6, 'F');
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        setCol(COL.title);
        pdf.text(text, M + 5, y + 4.5);
        y += 10;
      };

      // --- Helper: key-value row ---
      const kvRow = (label, value, opts = {}) => {
        checkPage(7);
        const rowH = 6.5;
        if (opts.highlight) {
          setFill(opts.bgColor || COL.cardBg);
          pdf.roundedRect(M, y - 1, cW, rowH + 1.5, 1.5, 1.5, 'F');
        }
        pdf.setFontSize(opts.small ? 8 : 9);
        pdf.setFont('helvetica', opts.labelBold ? 'bold' : 'normal');
        setCol(opts.labelColor || COL.text);
        pdf.text(opts.indent ? `   ${label}` : label, M + 3, y + 3.5);
        pdf.setFont('helvetica', opts.valueBold !== false ? 'bold' : 'normal');
        setCol(opts.valueColor || COL.title);
        pdf.text(String(value), M + cW - 3, y + 3.5, { align: 'right' });
        y += rowH + (opts.gap || 0.5);
      };

      // --- Helper: two-column key-value ---
      const kvRowTwo = (l1, v1, l2, v2, opts = {}) => {
        checkPage(7);
        const half = cW / 2 - 2;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        setCol(COL.text);
        pdf.text(l1, M + 3, y + 3.5);
        pdf.setFont('helvetica', 'bold');
        setCol(COL.title);
        pdf.text(String(v1), M + half - 2, y + 3.5, { align: 'right' });
        pdf.setFont('helvetica', 'normal');
        setCol(COL.text);
        pdf.text(l2, M + half + 6, y + 3.5);
        pdf.setFont('helvetica', 'bold');
        setCol(COL.title);
        pdf.text(String(v2), M + cW - 3, y + 3.5, { align: 'right' });
        y += 7;
      };

      // --- Helper: divider ---
      const divider = () => {
        setDraw(COL.divider);
        pdf.setLineWidth(0.3);
        pdf.line(M, y, M + cW, y);
        y += 3;
      };

      // === LOAD LOGO ===
      let logoData = null;
      let logoNatW = 1, logoNatH = 1;
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = '/logo-vad.jpg';
        });
        logoNatW = img.naturalWidth;
        logoNatH = img.naturalHeight;
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        logoData = c.toDataURL('image/jpeg', 0.85);
      } catch {}

      // ========================
      // PAGE 1: HEADER + INPUTS
      // ========================

      // Logo & Title
      if (logoData) {
        const logoW = 30;
        const logoH = logoW * (logoNatH / logoNatW);
        pdf.addImage(logoData, 'JPEG', M, y, logoW, logoH);
        y += logoH + 4;
      }
      y += 4; // extra padding above title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      setCol(COL.title);
      pdf.text(name || 'Business Planner', M, y);
      y += 6;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      setCol(COL.subtitle);
      pdf.text(`Generato il ${new Date().toLocaleDateString('it-IT')} | Anno ${selectedYear} | Vista ${timeView === 'monthly' ? 'Mensile' : 'Annuale'}`, M, y);
      y += 3;
      divider();
      y += 2;

      // ---- Obiettivi ----
      sectionTitle('Obiettivi di Fatturato', COL.accent);
      kvRowTwo('Anno 1', formatCurrency(data.objectives.revenue_target_y1), 'Anno 2', formatCurrency(data.objectives.revenue_target_y2));
      kvRow('Anno 3', formatCurrency(data.objectives.revenue_target_y3));
      y += 3;

      // ---- Ricavi e Offerta ----
      sectionTitle('Ricavi e Offerta', COL.green);
      kvRowTwo('Ticket Medio', formatCurrency(data.revenue.avg_ticket), 'Margine', formatPct(data.revenue.margin_pct));
      kvRowTwo('% Referral', formatPct(data.revenue.referral_pct), '% Upsell', formatPct(data.revenue.upsell_pct));
      kvRowTwo('Ticket Upsell', formatCurrency(data.revenue.upsell_avg_ticket), 'Margine Upsell', formatPct(data.revenue.upsell_margin_pct));
      if (data.revenue.recurring_pct > 0) {
        kvRowTwo('% Recurring', formatPct(data.revenue.recurring_pct), 'Ticket Recurring', formatCurrency(data.revenue.recurring_annual_ticket));
        kvRowTwo('Margine Recurring', formatPct(data.revenue.recurring_margin_pct), 'Churn Rate', formatPct(data.revenue.churn_rate));
      }
      y += 3;

      // ---- Funnel ----
      sectionTitle('Funnel di Vendita', COL.indigo);
      kvRowTwo('Conversione in vendita', formatPct(data.funnel.conversion_rate), 'Show-up', formatPct(data.funnel.show_up_rate));
      kvRowTwo('App.ti su lead', formatPct(data.funnel.appointment_rate), 'Ciclo vendita', `${data.funnel.sales_cycle_months || 0} mesi`);
      y += 3;

      // ---- Marketing ----
      sectionTitle('Marketing', COL.blue);
      kvRow('CAC Target', formatCurrency(data.marketing.cac_target));
      y += 3;

      // ---- Team ----
      sectionTitle('Team Commerciale', COL.purple);
      kvRowTwo('App.ti/setter/mese', formatNumber(data.team.setter_appointments_per_month), 'Fee/app.to setter', formatCurrency(data.team.setter_fee_per_appointment));
      kvRowTwo('App.ti/venditore/mese', formatNumber(data.team.closer_appointments_per_month), 'Comm. fissa vendita', formatCurrency(data.team.closer_commission_fixed));
      if (data.team.closer_commission_pct > 0) {
        kvRow('Commissione % vendita', formatPct(data.team.closer_commission_pct));
      }
      y += 3;

      // ---- Costi ----
      sectionTitle('Costi Fissi', COL.red);
      (data.costs.fixed_costs || []).forEach(fc => {
        kvRow(fc.name, fc.is_percentage ? `${fc.amount}% budget mkt` : formatCurrency(fc.amount) + (fc.type === 'monthly' ? '/mese' : '/anno'));
      });
      if (data.costs.custom_costs?.length > 0) {
        data.costs.custom_costs.forEach(cc => {
          kvRow(cc.name || 'Costo custom', formatCurrency(cc.amount) + (cc.type === 'monthly' ? '/mese' : '/anno'));
        });
      }

      // ========================
      // PAGE 2: DASHBOARD OUTPUT
      // ========================
      addPage();

      // Header
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      setCol(COL.title);
      pdf.text('Dashboard di Output', M, y);
      y += 4;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      setCol(COL.subtitle);
      pdf.text(`${name} | Anno ${selectedYear} | Vista ${timeView === 'monthly' ? 'Mensile' : 'Annuale'}`, M, y);
      y += 2;
      divider();
      y += 2;

      // ---- KPI Cards (drawn as colored boxes) ----
      const kpis = [
        { label: calc.hasMx ? 'Fatt. Acquisizione' : 'Fatturato', val: formatCurrency(calc.d.totalRevenue), col: COL.accent },
        ...(calc.hasMx ? [
          { label: 'Fatt. Massimizzazione', val: formatCurrency(calc.d.mxTotalRev), col: COL.purple },
          { label: 'Fatturato Totale', val: formatCurrency(calc.d.grandTotalRevenue), col: COL.blue },
        ] : []),
        { label: 'Vendite', val: formatNumber(calc.hasMx ? calc.d.salesNeeded + calc.mx.mxSales / (timeView === 'monthly' ? 12 : 1) : calc.d.salesNeeded), col: COL.amber },
        { label: 'Budget Mkt', val: formatCurrency(calc.d.marketingBudget), col: COL.blue },
        { label: 'Utile Netto', val: formatCurrency(calc.hasMx ? calc.d.grandNetProfit : calc.d.netProfit), col: (calc.hasMx ? calc.grandNetProfit : calc.netProfit) >= 0 ? COL.green : COL.red },
      ];

      const kpiW = (cW - (kpis.length - 1) * 3) / kpis.length;
      const kpiH = 18;
      kpis.forEach((kpi, i) => {
        const x = M + i * (kpiW + 3);
        // Card bg
        setFill([245, 245, 250]);
        setDraw([220, 220, 230]);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(x, y, kpiW, kpiH, 2, 2, 'FD');
        // Label
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'bold');
        setCol(COL.muted);
        pdf.text(kpi.label.toUpperCase(), x + 2.5, y + 5);
        // Value
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        setCol(kpi.col);
        pdf.text(kpi.val, x + 2.5, y + 13);
      });
      y += kpiH + 6;

      // ---- Breakdown Funnel ----
      sectionTitle('Breakdown Funnel', COL.indigo);
      const div = timeView === 'monthly' ? 12 : 1;
      const funnelRows = [
        { l: 'Investimento Marketing', v: formatCurrency(calc.d.marketingBudget), hl: true, bg: [224, 231, 255] },
        { l: 'Impression', v: formatNumber(calc.impressions / div) },
        { l: 'Click', v: formatNumber(calc.clicks / div) },
        { l: 'Visite pagina', v: formatNumber(calc.pageVisits / div) },
        { l: 'Lead totali', v: formatNumber(calc.leadsTotal / div) },
        { l: 'Lead in target', v: formatNumber(calc.leadsTarget / div), hl: true, bg: [219, 234, 254] },
        { l: '  Costo per lead target', v: formatCurrency(calc.costPerLeadTarget), small: true, vc: COL.blue },
        { l: 'App.ti fissati', v: formatNumber(calc.appointmentsScheduled / div), hl: true, bg: [254, 243, 199] },
        { l: '  Costo per app.to fissato', v: formatCurrency(calc.costPerApptScheduled), small: true, vc: COL.amber },
        { l: 'App.ti avvenuti', v: formatNumber(calc.appointmentsCompleted / div), hl: true, bg: [255, 237, 213] },
        { l: '  Costo per app.to avvenuto', v: formatCurrency(calc.costPerApptCompleted), small: true, vc: COL.accent },
        { l: 'Vendite', v: formatNumber(calc.d.salesNeeded), hl: true, bg: [209, 250, 229] },
      ];
      funnelRows.forEach(r => {
        kvRow(r.l, r.v, {
          highlight: r.hl,
          bgColor: r.bg,
          small: r.small,
          indent: r.small,
          labelBold: r.hl,
          valueColor: r.vc || (r.hl ? COL.title : COL.text),
          valueBold: true,
        });
      });
      y += 4;

      // ---- Breakdown Ricavi ----
      sectionTitle('Breakdown Ricavi', COL.green);
      kvRow('Vendite', formatNumber(calc.d.salesNeeded), { highlight: true, bgColor: COL.cardBg, labelBold: true });
      kvRow('Prima vendita', formatCurrency(calc.d.revenuePrimary));
      kvRow('Referral', formatCurrency(calc.d.revenueReferral));
      kvRow('Upsell', formatCurrency(calc.d.revenueUpsell));
      kvRow('Recurring', formatCurrency(calc.d.revenueRecurring));
      kvRow('Fatturato Totale', formatCurrency(calc.d.totalRevenue), { highlight: true, bgColor: [219, 234, 254], labelBold: true, valueColor: COL.blue });
      const costVarVendita = calc.d.revenuePrimary + calc.d.revenueReferral - calc.d.grossMarginPrimary;
      const costVarUpsell = calc.d.revenueUpsell - calc.d.grossMarginUpsell;
      const costVarRecurring = calc.d.revenueRecurring - calc.d.grossMarginRecurring;
      kvRow(`Costi variabili vendita (${formatPct(100 - (data.revenue.margin_pct || 0))})`, `-${formatCurrency(costVarVendita)}`, { small: true, valueColor: COL.red });
      kvRow(`Costi variabili upsell (${formatPct(100 - (data.revenue.upsell_margin_pct || 0))})`, `-${formatCurrency(costVarUpsell)}`, { small: true, valueColor: COL.red });
      kvRow(`Costi variabili recurring (${formatPct(100 - (data.revenue.recurring_margin_pct || 0))})`, `-${formatCurrency(costVarRecurring)}`, { small: true, valueColor: COL.red });
      kvRow('Margine Lordo', formatCurrency(calc.d.grossMargin), { highlight: true, bgColor: [209, 250, 229], labelBold: true, valueColor: COL.green });
      y += 4;

      // ---- Breakdown Team ----
      checkPage(50);
      sectionTitle('Breakdown Team', COL.blue);
      kvRowTwo('Setter necessari', String(calc.numSetters), 'Venditori necessari', String(calc.numClosers));
      kvRow('Totale risorse', String(calc.numSetters + calc.numClosers), { highlight: true, bgColor: [219, 234, 254], labelBold: true, valueColor: COL.blue });
      kvRowTwo('Guadagno setter/mese', formatCurrency(calc.setterEarning), 'Guadagno venditore/mese', formatCurrency(calc.closerEarning));
      kvRow('Costo Team', formatCurrency(calc.d.teamCost), { highlight: true, bgColor: [254, 226, 226], labelBold: true, valueColor: COL.red });
      y += 4;

      // ---- Breakdown Costi ----
      sectionTitle('Breakdown Costi', COL.red);
      kvRow('Marketing', formatCurrency(calc.d.marketingBudget));
      kvRow('Team Sales', formatCurrency(calc.d.teamCost));
      kvRow('Costi Fissi', formatCurrency(calc.d.otherCosts));
      kvRow('Totale Costi', formatCurrency(calc.d.totalCosts), { highlight: true, bgColor: [254, 226, 226], labelBold: true, valueColor: COL.red });
      y += 4;

      // ---- Massimizzazione (if active) ----
      if (calc.hasMx) {
        checkPage(50);
        sectionTitle('Massimizzazione Database', COL.accent);
        kvRow('Database lead disponibile', formatNumber(calc.mx.dbSize));
        kvRowTwo('App.ti fissati', formatNumber(calc.mx.mxAppointments), 'App.ti avvenuti', formatNumber(calc.mx.mxShowUp));
        kvRow('Vendite da massimizzazione', formatNumber(calc.mx.mxSales), { labelBold: true });
        kvRow('Fatturato recuperato', formatCurrency(calc.d.mxTotalRev), { highlight: true, bgColor: [255, 237, 213], valueColor: COL.accent, labelBold: true });
        kvRow('Margine lordo', formatCurrency(calc.d.mxGrossMargin), { valueColor: COL.green });
        kvRow(`Fee setter (${formatCurrency(calc.mx.mxFeePerAppt)}/appt)`, `-${formatCurrency(calc.d.mxSetterFeeCost)}`, { valueColor: COL.red });
        kvRow('Provvigioni venditori', `-${formatCurrency(calc.d.mxCloserCommCost)}`, { valueColor: COL.red });
        kvRow('Profitto netto mx', formatCurrency(calc.d.mxNetProfit), {
          highlight: true,
          bgColor: calc.mx.mxNetProfit >= 0 ? [209, 250, 229] : [254, 226, 226],
          labelBold: true,
          valueColor: calc.mx.mxNetProfit >= 0 ? COL.green : COL.red,
        });
        y += 4;
      }

      // ---- Profitto Finale ----
      checkPage(25);
      const finalProfit = calc.hasMx ? calc.grandNetProfit : calc.netProfit;
      const finalProfitD = calc.hasMx ? calc.d.grandNetProfit : calc.d.netProfit;
      const profitPositive = finalProfit >= 0;

      setFill(profitPositive ? [236, 253, 245] : [254, 242, 242]);
      setDraw(profitPositive ? [167, 243, 208] : [252, 165, 165]);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(M, y, cW, 20, 2.5, 2.5, 'FD');

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      setCol(COL.muted);
      pdf.text(`RIEPILOGO PROFITTO (${timeView === 'monthly' ? 'Mensile' : 'Annuale'}) - Anno ${selectedYear}`, M + 4, y + 6);

      // Detail line
      pdf.setFontSize(7.5);
      pdf.setFont('helvetica', 'normal');
      setCol(COL.text);
      const parts = [];
      parts.push(`Margine Lordo${calc.hasMx ? ' Acq.' : ''}: ${formatCurrency(calc.d.grossMargin)}`);
      if (calc.hasMx) parts.push(`+ Margine Max.: ${formatCurrency(calc.d.mxGrossMargin)}`);
      parts.push(`- Marketing: ${formatCurrency(calc.d.marketingBudget)}`);
      parts.push(`- Team: ${formatCurrency(calc.d.teamCost)}`);
      parts.push(`- Costi Fissi: ${formatCurrency(calc.d.otherCosts)}`);
      pdf.text(parts.join('  |  '), M + 4, y + 11);

      // Profit value
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      setCol(profitPositive ? COL.green : COL.red);
      pdf.text(`= ${formatCurrency(finalProfitD)}`, M + cW - 4, y + 14, { align: 'right' });
      y += 25;

      // ===================================
      // PAGE 3: PROIEZIONE TRIENNALE
      // ===================================
      addPage();

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      setCol(COL.title);
      pdf.text('Proiezione Triennale', M, y);
      y += 4;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      setCol(COL.subtitle);
      pdf.text(`${name} | Riepilogo comparativo Anno 1 - Anno 2 - Anno 3`, M, y);
      y += 2;
      divider();
      y += 4;

      // ---- 3-Year Recap Table ----
      const cd = calc.chartData; // [{name, nuovi, recurring, utile, vendite, totalRevenue, totalCosts, grossMargin, mktBudget, teamCost, mxSales, mxRev}, ...]

      // Table header
      const colX = [M + 3, M + cW * 0.38, M + cW * 0.58, M + cW * 0.79];
      const colW2 = cW * 0.20;

      setFill([30, 41, 59]);
      pdf.roundedRect(M, y, cW, 8, 1.5, 1.5, 'F');
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      setCol(COL.white);
      pdf.text('Metrica', colX[0], y + 5.5);
      pdf.text('Anno 1', colX[1] + colW2 / 2, y + 5.5, { align: 'center' });
      pdf.text('Anno 2', colX[2] + colW2 / 2, y + 5.5, { align: 'center' });
      pdf.text('Anno 3', colX[3] + colW2 / 2, y + 5.5, { align: 'center' });
      y += 10;

      // Table rows
      const tableRows = [
        { label: 'Fatturato Target', values: [formatCurrency(data.objectives.revenue_target_y1), formatCurrency(data.objectives.revenue_target_y2), formatCurrency(data.objectives.revenue_target_y3)], bold: true },
        { label: 'Vendite Necessarie', values: cd.map(r => formatNumber(r.vendite)) },
        { label: 'Fatturato Nuovi Clienti', values: cd.map(r => formatCurrency(r.nuovi)) },
        { label: 'Fatturato Recurring', values: cd.map(r => formatCurrency(r.recurring)) },
        { label: 'Fatturato Totale', values: cd.map(r => formatCurrency(r.totalRevenue)), bold: true, bg: [239, 246, 255] },
        ...(calc.hasMx ? [
          { label: 'Vendite Massimizzazione', values: cd.map(r => formatNumber(r.mxSales)) },
          { label: 'Fatturato Massimizzazione', values: cd.map(r => formatCurrency(r.mxRev)) },
          { label: 'Fatturato Complessivo', values: cd.map(r => formatCurrency(r.totalRevenue + r.mxRev)), bold: true, bg: [224, 231, 255] },
        ] : []),
        { label: 'Margine Lordo', values: cd.map(r => formatCurrency(r.grossMargin)), bg: [236, 253, 245] },
        { label: 'Costi Marketing', values: cd.map(r => formatCurrency(r.mktBudget)), valColor: COL.red },
        { label: 'Costi Team', values: cd.map(r => formatCurrency(r.teamCost)), valColor: COL.red },
        { label: 'Costi Fissi', values: cd.map(() => formatCurrency(calc.totalOtherCostsYr)), valColor: COL.red },
        { label: 'Costi Totali', values: cd.map(r => formatCurrency(r.totalCosts)), bold: true, bg: [254, 242, 242], valColor: COL.red },
        { label: 'Utile Netto', values: cd.map(r => formatCurrency(r.utile)), bold: true, bg: cd[0].utile >= 0 ? [236, 253, 245] : [254, 242, 242] },
      ];

      tableRows.forEach((row, idx) => {
        checkPage(8);
        const rowH = 7;
        if (row.bg) {
          setFill(row.bg);
          pdf.rect(M, y - 0.5, cW, rowH, 'F');
        } else if (idx % 2 === 0) {
          setFill([250, 250, 252]);
          pdf.rect(M, y - 0.5, cW, rowH, 'F');
        }
        // Label
        pdf.setFontSize(8);
        pdf.setFont('helvetica', row.bold ? 'bold' : 'normal');
        setCol(COL.text);
        pdf.text(row.label, colX[0], y + 4);
        // Values
        pdf.setFont('helvetica', row.bold ? 'bold' : 'normal');
        row.values.forEach((v, vi) => {
          setCol(row.valColor || (row.bold ? COL.title : COL.text));
          pdf.text(v, colX[vi + 1] + colW2 / 2, y + 4, { align: 'center' });
        });
        y += rowH;
      });
      y += 6;

      // ---- 3-Year Bar Chart ----
      checkPage(75);
      sectionTitle('Crescita Fatturato e Utile', COL.amber);

      const chartH = 55;
      const chartW = cW - 10;
      const chartX = M + 5;
      const chartY = y;
      const chartBottom = chartY + chartH;

      // Find max value for scale
      const allVals = cd.flatMap(r => [r.nuovi + r.recurring, Math.abs(r.utile)]);
      const maxVal = Math.max(...allVals, 1);
      const scale = chartH / (maxVal * 1.15);

      // Draw axes
      setDraw([200, 200, 210]);
      pdf.setLineWidth(0.3);
      pdf.line(chartX, chartBottom, chartX + chartW, chartBottom); // x-axis

      // Y-axis gridlines
      const gridSteps = 4;
      for (let i = 0; i <= gridSteps; i++) {
        const gy = chartBottom - (chartH / gridSteps) * i;
        setDraw([235, 235, 240]);
        pdf.setLineWidth(0.15);
        pdf.line(chartX, gy, chartX + chartW, gy);
        // Label
        const gVal = (maxVal * 1.15 / gridSteps) * i;
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'normal');
        setCol(COL.muted);
        const tickLabel = gVal >= 1000000 ? `${(gVal / 1000000).toFixed(1)}M` : gVal >= 1000 ? `${(gVal / 1000).toFixed(0)}k` : formatNumber(gVal);
        pdf.text(`€${tickLabel}`, chartX - 2, gy + 1.5, { align: 'right' });
      }

      // Bars
      const groupW = chartW / 3;
      const barW = groupW * 0.22;
      const gap = groupW * 0.04;

      cd.forEach((yr, i) => {
        const gx = chartX + i * groupW + groupW * 0.15;

        // Bar: Nuovi (orange)
        const hNuovi = yr.nuovi * scale;
        setFill([249, 115, 22]);
        if (hNuovi > 0) pdf.roundedRect(gx, chartBottom - hNuovi, barW, hNuovi, 1, 1, 'F');

        // Bar: Recurring (indigo)
        const hRec = yr.recurring * scale;
        setFill([99, 102, 241]);
        if (hRec > 0) pdf.roundedRect(gx + barW + gap, chartBottom - hRec, barW, hRec, 1, 1, 'F');

        // Bar: Utile (green/red)
        const hUtile = Math.abs(yr.utile) * scale;
        setFill(yr.utile >= 0 ? [16, 185, 129] : [239, 68, 68]);
        if (hUtile > 0) pdf.roundedRect(gx + (barW + gap) * 2, chartBottom - hUtile, barW, hUtile, 1, 1, 'F');

        // Year label
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        setCol(COL.title);
        pdf.text(yr.name, gx + (barW * 1.5 + gap), chartBottom + 5, { align: 'center' });
      });

      y = chartBottom + 9;

      // Legend
      const legendItems = [
        { label: 'Nuovi Clienti', color: [249, 115, 22] },
        { label: 'Recurring', color: [99, 102, 241] },
        { label: 'Utile Netto', color: [16, 185, 129] },
      ];
      let lx = chartX;
      legendItems.forEach(item => {
        setFill(item.color);
        pdf.roundedRect(lx, y, 3, 3, 0.5, 0.5, 'F');
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        setCol(COL.text);
        pdf.text(item.label, lx + 5, y + 2.5);
        lx += 35;
      });
      y += 8;

      // ---- Highlight box: 3-year totals ----
      checkPage(22);
      const tot3Rev = cd.reduce((s, r) => s + r.totalRevenue + (r.mxRev || 0), 0);
      const tot3Profit = cd.reduce((s, r) => s + r.utile, 0);
      const tot3Sales = cd.reduce((s, r) => s + r.vendite, 0);

      setFill([249, 250, 251]);
      setDraw([209, 213, 219]);
      pdf.setLineWidth(0.4);
      pdf.roundedRect(M, y, cW, 16, 2, 2, 'FD');

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      setCol(COL.muted);
      pdf.text('TOTALE 3 ANNI', M + 4, y + 5);

      const sumX = [M + cW * 0.3, M + cW * 0.55, M + cW * 0.8];
      const sumLabels = ['Fatturato Complessivo', 'Vendite Totali', 'Utile Cumulato'];
      const sumVals = [formatCurrency(tot3Rev), formatNumber(tot3Sales), formatCurrency(tot3Profit)];
      const sumCols = [COL.blue, COL.amber, tot3Profit >= 0 ? COL.green : COL.red];

      sumLabels.forEach((lbl, i) => {
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'normal');
        setCol(COL.muted);
        pdf.text(lbl, sumX[i], y + 5, { align: 'center' });
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        setCol(sumCols[i]);
        pdf.text(sumVals[i], sumX[i], y + 12, { align: 'center' });
      });
      y += 20;

      // Save
      pdf.save(`${(name || 'planner').replace(/\s+/g, '_')}_report.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      toast({ title: 'Errore esportazione PDF', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };
  useEffect(() => {
    if (isNew || !id || !name.trim()) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      setSaving(true);
      updatePlanner(id, { name, client_id: clientId || null, data })
        .catch(() => {})
        .finally(() => setSaving(false));
    }, 1200);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [data, name, clientId]);

  // =================== CALCULATION ENGINE ===================
  const calc = useMemo(() => {
    const o = data.objectives;
    const r = data.revenue;
    const f = data.funnel;
    const m = data.marketing;
    const t = data.team;
    const c = data.costs;

    const revenueTarget = [o.revenue_target_y1, o.revenue_target_y2, o.revenue_target_y3][selectedYear - 1] || 0;
    const avgTicket = r.avg_ticket || 0;
    const marginPct = r.margin_pct || 0;
    const upsellMarginPct = r.upsell_margin_pct || 0;
    const recurringPct = r.recurring_pct || 0;
    const recurringTicket = r.recurring_annual_ticket || 0;
    const recurringMarginPct = r.recurring_margin_pct || 0;
    const churnRate = r.churn_rate || 0;
    const retention = 1 - churnRate / 100;

    // Effective ticket per customer
    const referralRevPerCustomer = avgTicket * (r.referral_pct || 0) / 100;
    const upsellRevPerCustomer = (r.upsell_avg_ticket || 0) * (r.upsell_pct || 0) / 100;
    const upsellInTarget = r.upsell_in_target !== false;
    // If upsell concorre al target: include in denominator (fewer sales needed)
    // If upsell NON concorre: exclude from denominator (upsell adds on top)
    const effectiveTicket = avgTicket + referralRevPerCustomer + (upsellInTarget ? upsellRevPerCustomer : 0);

    // Sales needed for each year (from new customer targets)
    const salesByYear = [o.revenue_target_y1, o.revenue_target_y2, o.revenue_target_y3].map(
      target => effectiveTicket > 0 ? Math.ceil((target || 0) / effectiveTicket) : 0
    );

    const salesNeeded = salesByYear[selectedYear - 1] || 0;
    const revenuePrimary = salesNeeded * avgTicket;
    const revenueReferral = salesNeeded * referralRevPerCustomer;
    const revenueUpsell = salesNeeded * upsellRevPerCustomer;
    const revenueNewCustomers = revenuePrimary + revenueReferral + revenueUpsell;

    // Recurring revenue: based on new customers or upsell customers from previous years
    const recurringBase = r.recurring_base || 'new';
    // If recurring is based on upsell, the pool is salesByYear[y] * upsellPct/100
    const recurringPoolByYear = salesByYear.map(s =>
      recurringBase === 'upsell' ? s * (r.upsell_pct || 0) / 100 : s
    );

    let revenueRecurring = 0;
    if (selectedYear >= 2) {
      const recurringFromY1 = recurringPoolByYear[0] * (recurringPct / 100) * Math.pow(retention, selectedYear - 2);
      revenueRecurring += recurringFromY1 * recurringTicket;
    }
    if (selectedYear >= 3) {
      const recurringFromY2 = recurringPoolByYear[1] * (recurringPct / 100);
      revenueRecurring += recurringFromY2 * recurringTicket;
    }

    const totalRevenue = revenueNewCustomers + revenueRecurring;

    // Margine separato: primario/referral con margine base, upsell con margine upsell, recurring con margine recurring
    const grossMarginPrimary = (revenuePrimary + revenueReferral) * (marginPct / 100);
    const grossMarginUpsell = revenueUpsell * (upsellMarginPct / 100);
    const grossMarginRecurring = revenueRecurring * (recurringMarginPct / 100);
    const grossMargin = grossMarginPrimary + grossMarginUpsell + grossMarginRecurring;

    // Funnel
    const convRate = f.conversion_rate || 0;
    const showRate = f.show_up_rate || 0;
    const apptRate = f.appointment_rate || 0;
    const leadTargetPct = f.lead_target_pct || 90;
    const clickToVisitPct = f.click_to_visit_pct || 80;
    const optinPct = f.optin_conversion_pct || 10;

    const appointmentsCompleted = convRate > 0 ? Math.ceil(salesNeeded / (convRate / 100)) : 0;
    const appointmentsScheduled = showRate > 0 ? Math.ceil(appointmentsCompleted / (showRate / 100)) : 0;
    const leadsTarget = apptRate > 0 ? Math.ceil(appointmentsScheduled / (apptRate / 100)) : 0;
    const leadsTotal = leadTargetPct > 0 ? Math.ceil(leadsTarget / (leadTargetPct / 100)) : 0;
    const pageVisits = optinPct > 0 ? Math.ceil(leadsTotal / (optinPct / 100)) : 0;
    const clicks = clickToVisitPct > 0 ? Math.ceil(pageVisits / (clickToVisitPct / 100)) : 0;
    const ctrPct = f.ctr_pct || 1;
    const impressions = ctrPct > 0 ? Math.ceil(clicks / (ctrPct / 100)) : 0;

    // ===== MASSIMIZZAZIONE (calcolato prima del team per impattarne il dimensionamento) =====
    const mx = data.maximization || {};
    const dbSize = leadsTotal > salesNeeded ? leadsTotal - salesNeeded : 0;
    const mxApptRate = (mx.db_appointment_rate || 0) / 100;
    const mxShowUpRate = (mx.db_show_up_rate || 50) / 100;
    const mxConvRate = convRate / 100;
    const mxFeePerAppt = mx.mx_fee_per_appointment ?? 100;

    const mxAppointments = Math.floor(dbSize * mxApptRate);
    const mxShowUp = Math.floor(mxAppointments * mxShowUpRate);
    const mxSales = Math.floor(mxShowUp * mxConvRate);
    const mxRevenue = mxSales * avgTicket;
    const mxUpsellRev = mxSales * upsellRevPerCustomer;
    const mxTotalRev = mxRevenue + mxUpsellRev;
    const mxGrossMargin = mxRevenue * (marginPct / 100) + mxUpsellRev * (upsellMarginPct / 100);

    // Marketing
    const cac = m.cac_target || 0;
    const marketingBudget = salesNeeded * cac;
    const costPerLeadTarget = leadsTarget > 0 ? marketingBudget / leadsTarget : 0;
    const costPerApptScheduled = appointmentsScheduled > 0 ? marketingBudget / appointmentsScheduled : 0;
    const costPerApptCompleted = appointmentsCompleted > 0 ? marketingBudget / appointmentsCompleted : 0;

    // Team (dimensionamento basato su appuntamenti AVVENUTI/show-up, include massimizzazione)
    const totalMonthlyShowUp = (appointmentsCompleted + mxShowUp) / 12;
    const monthlyBaseShowUp = appointmentsCompleted / 12;
    const monthlyMxShowUp = mxShowUp / 12;
    const monthlySales = salesNeeded / 12;
    const monthlyMxSales = mxSales / 12;

    const numSetters = t.setter_appointments_per_month > 0 ? Math.ceil(totalMonthlyShowUp / t.setter_appointments_per_month) : 0;
    const numClosers = t.closer_appointments_per_month > 0 ? Math.ceil(totalMonthlyShowUp / t.closer_appointments_per_month) : 0;

    const apptsPerSetter = numSetters > 0 ? totalMonthlyShowUp / numSetters : 0;
    const baseApptsPerSetter = numSetters > 0 ? monthlyBaseShowUp / numSetters : 0;
    const mxApptsPerSetter = numSetters > 0 ? monthlyMxShowUp / numSetters : 0;
    const apptsPerCloser = numClosers > 0 ? totalMonthlyShowUp / numClosers : 0;
    const salesPerCloser = numClosers > 0 ? monthlySales / numClosers : 0;
    const mxSalesPerCloser = numClosers > 0 ? monthlyMxSales / numClosers : 0;

    // Setter: fisso + fee per appuntamento AVVENUTO (show-up) + fee mx per show-up massimizzazione
    const setterEarning = (t.setter_fixed_salary || 0) + (t.setter_fee_per_appointment || 0) * baseApptsPerSetter + mxFeePerAppt * mxApptsPerSetter;
    // Commissioni venditori: vendite primarie + upsell + recurring + massimizzazione
    const closerCommPerSale = (t.closer_commission_fixed || 0) + (t.closer_commission_pct || 0) / 100 * avgTicket;
    const upsellSalesPerCloserMo = numClosers > 0 ? (salesNeeded * (r.upsell_pct || 0) / 100) / 12 / numClosers : 0;
    const closerUpsellCommPerSale = (t.closer_upsell_commission_fixed || 0) + (t.closer_upsell_commission_pct || 0) / 100 * (r.upsell_avg_ticket || 0);
    const recurringSalesPerCloserMo = numClosers > 0 ? (revenueRecurring / (recurringTicket || 1)) / 12 / numClosers : 0;
    const closerRecurringCommPerSale = (t.closer_recurring_commission_fixed || 0) + (t.closer_recurring_commission_pct || 0) / 100 * recurringTicket;
    const mxUpsellSalesPerCloserMo = numClosers > 0 ? (mxSales * (r.upsell_pct || 0) / 100) / 12 / numClosers : 0;
    const closerEarning = (t.closer_fixed_salary || 0) + closerCommPerSale * salesPerCloser + closerUpsellCommPerSale * upsellSalesPerCloserMo + closerRecurringCommPerSale * recurringSalesPerCloserMo + closerCommPerSale * mxSalesPerCloser + closerUpsellCommPerSale * mxUpsellSalesPerCloserMo;

    const totalSetterCostMo = numSetters * setterEarning;
    const totalCloserCostMo = numClosers * closerEarning;
    const totalTeamCostMo = totalSetterCostMo + totalCloserCostMo;
    const totalTeamCostYr = totalTeamCostMo * 12;

    // Costi specifici massimizzazione (per breakdown)
    const mxSetterFeeCost = mxAppointments * mxFeePerAppt;
    const mxCloserCommCost = mxSales * closerCommPerSale + (mxSales * (r.upsell_pct || 0) / 100) * closerUpsellCommPerSale;
    const mxTotalCost = mxSetterFeeCost + mxCloserCommCost;
    const mxNetProfit = mxGrossMargin - mxTotalCost;

    // Costs
    let fixedCostsMo = 0;
    (c.fixed_costs || []).forEach(fc => {
      let amt = fc.amount || 0;
      if (fc.is_percentage) amt = (marketingBudget / 12) * (amt / 100);
      fixedCostsMo += fc.type === 'monthly' ? amt : amt / 12;
    });
    let customCostsMo = 0;
    (c.custom_costs || []).forEach(cc => {
      customCostsMo += cc.type === 'monthly' ? (cc.amount || 0) : (cc.amount || 0) / 12;
    });
    const totalOtherCostsMo = fixedCostsMo + customCostsMo;
    const totalOtherCostsYr = totalOtherCostsMo * 12;

    // Profit
    const totalCostsYr = marketingBudget + totalTeamCostYr + totalOtherCostsYr;
    const netProfit = grossMargin - totalCostsYr;
    const cacComplessivo = salesNeeded > 0 ? (marketingBudget + totalTeamCostYr) / salesNeeded : 0;

    // Grand totals (acquisition + maximization)
    const grandTotalRevenue = totalRevenue + mxTotalRev;
    const grandGrossMargin = grossMargin + mxGrossMargin;
    const grandNetProfit = grandGrossMargin - totalCostsYr;

    // Warnings
    const warnings = [];
    if (cac > 0 && avgTicket > 0 && cac > avgTicket * 0.5) warnings.push({ level: 'warning', msg: 'CAC elevato rispetto al ticket medio' });
    if (marginPct > 0 && marginPct < 15) warnings.push({ level: 'warning', msg: 'Margine molto basso (< 15%)' });
    if (grandNetProfit < 0 && grandTotalRevenue > 0) warnings.push({ level: 'danger', msg: 'Modello in perdita: i costi superano il margine lordo' });
    if (numSetters > 0) {
      if (setterEarning < 2000) warnings.push({ level: 'danger', msg: `Guadagno setter basso: ${formatCurrency(setterEarning)}/mese` });
    }
    if (numClosers > 0) {
      if (closerEarning < 3000) warnings.push({ level: 'danger', msg: `Guadagno venditore basso: ${formatCurrency(closerEarning)}/mese` });
      else if (closerEarning < 4500) warnings.push({ level: 'warning', msg: `Guadagno venditore medio-basso: ${formatCurrency(closerEarning)}/mese` });
    }
    if (f.conversion_rate > 0 && f.show_up_rate > 0 && f.appointment_rate > 0) {
      const totalConv = (f.appointment_rate / 100) * (f.show_up_rate / 100) * (f.conversion_rate / 100);
      if (totalConv < 0.005) warnings.push({ level: 'warning', msg: 'Tasso di conversione complessivo del funnel molto basso' });
    }

    const div = timeView === 'monthly' ? 12 : 1;

    const hasMx = (data.maximization?.db_appointment_rate || 0) > 0;

    // Chart data: ricavi e utile per anno (con churn corretto)
    const chartData = [1, 2, 3].map(y => {
      const ySales = salesByYear[y - 1];
      const yRevPrimary = ySales * avgTicket;
      const yRevReferral = ySales * referralRevPerCustomer;
      const yRevUpsell = ySales * upsellRevPerCustomer;
      const yNewRev = yRevPrimary + yRevReferral + yRevUpsell;
      let yRecRev = 0;
      if (y >= 2) yRecRev += recurringPoolByYear[0] * (recurringPct / 100) * Math.pow(retention, y - 2) * recurringTicket;
      if (y >= 3) yRecRev += recurringPoolByYear[1] * (recurringPct / 100) * recurringTicket;
      const yGM = (yRevPrimary + yRevReferral) * (marginPct / 100) + yRevUpsell * (upsellMarginPct / 100) + yRecRev * (recurringMarginPct / 100);
      const yApptCompleted = convRate > 0 ? Math.ceil(ySales / (convRate / 100)) : 0;
      const yApptScheduled = showRate > 0 ? Math.ceil(yApptCompleted / (showRate / 100)) : 0;
      const yLeadsTarget = apptRate > 0 ? Math.ceil(yApptScheduled / (apptRate / 100)) : 0;
      const yLeadsTotal = leadTargetPct > 0 ? Math.ceil(yLeadsTarget / (leadTargetPct / 100)) : 0;
      const yDbSize = yLeadsTotal > ySales ? yLeadsTotal - ySales : 0;
      const yMxAppts = Math.floor(yDbSize * mxApptRate);
      const yMxShowUp = Math.floor(yMxAppts * mxShowUpRate);
      const yMxSales = Math.floor(yMxShowUp * mxConvRate);
      const yMxGM = yMxSales * avgTicket * (marginPct / 100) + yMxSales * upsellRevPerCustomer * (upsellMarginPct / 100);
      const yMktBudget = ySales * cac;
      // Team sizing based on show-up (avvenuti)
      const yTotalMonthlyShowUp = (yApptCompleted + yMxShowUp) / 12;
      const yMonthlyBaseShowUp = yApptCompleted / 12;
      const yMonthlyMxShowUp = yMxShowUp / 12;
      const yMonthlySales = ySales / 12;
      const yMonthlyMxSales = yMxSales / 12;
      const yNS = t.setter_appointments_per_month > 0 ? Math.ceil(yTotalMonthlyShowUp / t.setter_appointments_per_month) : 0;
      const yNC = t.closer_appointments_per_month > 0 ? Math.ceil(yTotalMonthlyShowUp / t.closer_appointments_per_month) : 0;
      const yBAS = yNS > 0 ? yMonthlyBaseShowUp / yNS : 0;
      const yMAS = yNS > 0 ? yMonthlyMxShowUp / yNS : 0;
      const ySPC = yNC > 0 ? yMonthlySales / yNC : 0;
      const yMSPC = yNC > 0 ? yMonthlyMxSales / yNC : 0;
      const yUSPC = yNC > 0 ? (ySales * (r.upsell_pct || 0) / 100) / 12 / yNC : 0;
      const yMUSPC = yNC > 0 ? (yMxSales * (r.upsell_pct || 0) / 100) / 12 / yNC : 0;
      const yRSPC = yNC > 0 && recurringTicket > 0 ? (yRecRev / recurringTicket) / 12 / yNC : 0;
      const ySE = (t.setter_fixed_salary || 0) + (t.setter_fee_per_appointment || 0) * yBAS + mxFeePerAppt * yMAS;
      const yCE = (t.closer_fixed_salary || 0) + closerCommPerSale * ySPC + closerUpsellCommPerSale * yUSPC + closerRecurringCommPerSale * yRSPC + closerCommPerSale * yMSPC + closerUpsellCommPerSale * yMUSPC;
      const yTeamCost = (yNS * ySE + yNC * yCE) * 12;
      const yTotalCosts = yMktBudget + yTeamCost + totalOtherCostsYr;
      const yProfit = yGM + yMxGM - yTotalCosts;
      return { name: `Anno ${y}`, nuovi: yNewRev, recurring: yRecRev, utile: yProfit, vendite: ySales, totalRevenue: yNewRev + yRecRev, totalCosts: yTotalCosts, grossMargin: yGM + yMxGM, mktBudget: yMktBudget, teamCost: yTeamCost, mxSales: yMxSales, mxRev: yMxSales * avgTicket + yMxSales * upsellRevPerCustomer };
    });

    return {
      revenueTarget, salesNeeded, revenuePrimary, revenueReferral, revenueUpsell, revenueRecurring, revenueNewCustomers, totalRevenue,
      grossMargin, grossMarginPrimary, grossMarginUpsell, grossMarginRecurring,
      appointmentsCompleted, appointmentsScheduled, leadsTarget, leadsTotal, pageVisits, clicks, impressions,
      marketingBudget, cac, costPerLeadTarget, costPerApptScheduled, costPerApptCompleted, cacComplessivo,
      numSetters, numClosers, setterEarning, closerEarning, apptsPerSetter, apptsPerCloser, salesPerCloser,
      totalTeamCostMo, totalTeamCostYr, totalSetterCostMo, totalCloserCostMo,
      totalOtherCostsMo, totalOtherCostsYr, totalCostsYr, netProfit, warnings, hasMx, chartData,
      // Maximization
      mx: { dbSize, mxAppointments, mxShowUp, mxSales, mxRevenue, mxUpsellRev, mxTotalRev, mxGrossMargin, mxFeePerAppt, mxSetterFeeCost, mxCloserCommCost, mxTotalCost, mxNetProfit },
      grandTotalRevenue, grandGrossMargin, grandNetProfit,
      // Time-adjusted values
      d: {
        salesNeeded: salesNeeded / div,
        totalRevenue: totalRevenue / div,
        revenueNewCustomers: revenueNewCustomers / div,
        revenueRecurring: revenueRecurring / div,
        grossMargin: grossMargin / div,
        grossMarginPrimary: grossMarginPrimary / div,
        grossMarginUpsell: grossMarginUpsell / div,
        grossMarginRecurring: grossMarginRecurring / div,
        marketingBudget: marketingBudget / div,
        teamCost: totalTeamCostYr / div,
        otherCosts: totalOtherCostsYr / div,
        totalCosts: totalCostsYr / div,
        netProfit: netProfit / div,
        revenuePrimary: revenuePrimary / div,
        revenueReferral: revenueReferral / div,
        revenueUpsell: revenueUpsell / div,
        // Maximization
        mxTotalRev: mxTotalRev / div,
        mxGrossMargin: mxGrossMargin / div,
        mxSetterFeeCost: mxSetterFeeCost / div,
        mxCloserCommCost: mxCloserCommCost / div,
        mxTotalCost: mxTotalCost / div,
        mxNetProfit: mxNetProfit / div,
        grandTotalRevenue: grandTotalRevenue / div,
        grandGrossMargin: grandGrossMargin / div,
        grandNetProfit: grandNetProfit / div,
      }
    };
  }, [data, selectedYear, timeView]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="planner-editor">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/toolkit')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            {!isReadOnly ? (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-xl font-bold border border-dashed border-gray-300 rounded-lg px-3 py-1.5 h-auto bg-white hover:border-gray-400 focus:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-300 transition-colors"
                placeholder="Clicca per inserire il nome del planner..."
                data-testid="planner-name-input"
              />
            ) : (
              <h1 className="text-xl font-bold text-gray-900 truncate">{name}</h1>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isReadOnly && (
            <div className="w-[220px]">
              <Combobox
                options={clients.map(c => ({ value: c.id, label: c.company_name }))}
                value={clientId}
                onChange={setClientId}
                placeholder="Associa cliente..."
                searchPlaceholder="Cerca cliente..."
                emptyText="Nessun cliente"
              />
            </div>
          )}
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={saving} variant={isNew ? 'default' : 'outline'} data-testid="save-planner-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Salvataggio...' : isNew ? 'Crea Planner' : 'Salvato'}
            </Button>
          )}
          <Button variant="outline" onClick={handleExportPDF} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? 'Esportazione...' : 'PDF'}
          </Button>
          {isReadOnly && (
            <Badge variant="secondary" className="text-sm px-3 py-1">Sola lettura</Badge>
          )}
        </div>
      </div>

      {/* Warnings */}
      {calc.warnings.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {calc.warnings.map((w, i) => <WarningBadge key={i} level={w.level}>{w.msg}</WarningBadge>)}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 w-full">
          <TabsTrigger value="ricavi" data-testid="tab-ricavi">Obiettivi e Ricavi</TabsTrigger>
          <TabsTrigger value="funnel" data-testid="tab-funnel">Funnel</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
          <TabsTrigger value="costi" data-testid="tab-costi">Costi</TabsTrigger>
          <TabsTrigger value="maximization" data-testid="tab-maximization">Massimizzazione</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
        </TabsList>

        {/* ============ OBIETTIVI E RICAVI ============ */}
        <TabsContent value="ricavi">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">
              {/* Fatturato Target */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-orange-600" /> Obiettivi di Fatturato Nuovi Clienti</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <NumInput label="Anno 1" prefix="€" value={data.objectives.revenue_target_y1} onChange={(v) => updateSection('objectives', 'revenue_target_y1', v)} step={1000} />
                    <NumInput label="Anno 2" prefix="€" value={data.objectives.revenue_target_y2} onChange={(v) => updateSection('objectives', 'revenue_target_y2', v)} step={1000} />
                    <NumInput label="Anno 3" prefix="€" value={data.objectives.revenue_target_y3} onChange={(v) => updateSection('objectives', 'revenue_target_y3', v)} step={1000} />
                  </div>
                </CardContent>
              </Card>
              {/* Ricavi e Offerta */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><CircleDollarSign className="w-5 h-5 text-orange-600" /> Ricavi e Offerta</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <NumInput label="Ticket Medio Vendita" prefix="€" value={data.revenue.avg_ticket} onChange={(v) => updateSection('revenue', 'avg_ticket', v)} step={100} />
                    <NumInput label="Margine (%)" suffix="%" value={data.revenue.margin_pct} onChange={(v) => updateSection('revenue', 'margin_pct', v)} step={1} />
                    <NumInput label="% Referral" suffix="%" value={data.revenue.referral_pct} onChange={(v) => updateSection('revenue', 'referral_pct', v)} step={1} />
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-500">Upsell</p>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="upsell-in-target"
                          checked={data.revenue.upsell_in_target !== false}
                          onCheckedChange={(checked) => updateSection('revenue', 'upsell_in_target', checked)}
                        />
                        <Label htmlFor="upsell-in-target" className="text-xs text-gray-500 cursor-pointer">Concorre al fatturato obiettivo</Label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <NumInput label="% Upsell" suffix="%" value={data.revenue.upsell_pct} onChange={(v) => updateSection('revenue', 'upsell_pct', v)} step={1} />
                      <NumInput label="Ticket Medio Upsell" prefix="€" value={data.revenue.upsell_avg_ticket} onChange={(v) => updateSection('revenue', 'upsell_avg_ticket', v)} step={100} />
                      <NumInput label="Margine Upsell (%)" suffix="%" value={data.revenue.upsell_margin_pct} onChange={(v) => updateSection('revenue', 'upsell_margin_pct', v)} step={1} />
                      <NumInput label="Ciclo Upsell (mesi)" value={data.revenue.upsell_cycle_months} onChange={(v) => updateSection('revenue', 'upsell_cycle_months', v)} />
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-500 mb-3">Recurring</p>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="recurring-new"
                          checked={data.revenue.recurring_base !== 'upsell'}
                          onCheckedChange={() => updateSection('revenue', 'recurring_base', 'new')}
                        />
                        <Label htmlFor="recurring-new" className="text-sm cursor-pointer">Su nuovi clienti</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="recurring-upsell"
                          checked={data.revenue.recurring_base === 'upsell'}
                          onCheckedChange={() => updateSection('revenue', 'recurring_base', 'upsell')}
                        />
                        <Label htmlFor="recurring-upsell" className="text-sm cursor-pointer">Su clienti upsell</Label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <NumInput label="% Recurring" suffix="%" value={data.revenue.recurring_pct} onChange={(v) => updateSection('revenue', 'recurring_pct', v)} step={1} hint="% clienti che diventano ricorrenti" />
                      <NumInput label="Ticket Annuo Recurring" prefix="€" value={data.revenue.recurring_annual_ticket} onChange={(v) => updateSection('revenue', 'recurring_annual_ticket', v)} step={100} />
                      <NumInput label="Margine Recurring (%)" suffix="%" value={data.revenue.recurring_margin_pct} onChange={(v) => updateSection('revenue', 'recurring_margin_pct', v)} step={1} />
                      <NumInput label="Churn Rate (%)" suffix="%" value={data.revenue.churn_rate} onChange={(v) => updateSection('revenue', 'churn_rate', v)} step={1} hint="% clienti persi ogni anno" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Output */}
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-sm text-gray-500 uppercase tracking-wide">Output Ricavi (Anno {selectedYear})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                    <span className="text-sm text-gray-600">Vendite nuovi clienti</span>
                    <span className="font-bold text-lg">{formatNumber(calc.salesNeeded)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                    <span className="text-sm text-gray-600">Ricavi da prima vendita</span>
                    <span className="font-semibold">{formatCurrency(calc.revenuePrimary)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                    <span className="text-sm text-gray-600">Ricavi da referral</span>
                    <span className="font-semibold">{formatCurrency(calc.revenueReferral)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                    <span className="text-sm text-gray-600">Ricavi da upsell</span>
                    <span className="font-semibold">{formatCurrency(calc.revenueUpsell)}</span>
                  </div>
                  {calc.revenueRecurring > 0 && (
                    <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                      <span className="text-sm font-medium text-indigo-800">Ricavi da recurring</span>
                      <span className="font-bold text-indigo-800">{formatCurrency(calc.revenueRecurring)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center p-3 bg-orange-100 rounded-lg border border-orange-200">
                    <span className="text-sm font-medium text-orange-800">Ricavo Totale</span>
                    <span className="font-bold text-lg text-orange-800">{formatCurrency(calc.totalRevenue)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg border border-green-200">
                    <span className="text-sm font-medium text-green-800">Margine Lordo</span>
                    <span className="font-bold text-lg text-green-800">{formatCurrency(calc.grossMargin)}</span>
                  </div>
                </div>
                {/* Year selector */}
                <div className="flex gap-2">
                  {[1, 2, 3].map(y => (
                    <Button key={y} size="sm" variant={selectedYear === y ? 'default' : 'outline'} onClick={() => setSelectedYear(y)}>Anno {y}</Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ FUNNEL ============ */}
        <TabsContent value="funnel">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><ArrowDownRight className="w-5 h-5 text-orange-600" /> Funnel di Vendita</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <NumInput label="% Conversione in vendita" suffix="%" value={data.funnel.conversion_rate} onChange={(v) => updateSection('funnel', 'conversion_rate', v)} step={1} />
                  <NumInput label="% Show-up" suffix="%" value={data.funnel.show_up_rate} onChange={(v) => updateSection('funnel', 'show_up_rate', v)} step={1} />
                  <NumInput label="% Appuntamenti fissati su lead" suffix="%" value={data.funnel.appointment_rate} onChange={(v) => updateSection('funnel', 'appointment_rate', v)} step={1} />
                  <NumInput label="Ciclo di vendita (mesi)" value={data.funnel.sales_cycle_months} onChange={(v) => updateSection('funnel', 'sales_cycle_months', v)} />
                </div>
                {/* Advanced funnel */}
                <Collapsible open={showAdvancedFunnel} onOpenChange={setShowAdvancedFunnel}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between text-gray-500">
                      Funnel Avanzato
                      <ChevronDown className={`w-4 h-4 transition-transform ${showAdvancedFunnel ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-3">
                    <p className="text-xs text-gray-400">Se non modificati, restano i valori di default</p>
                    <NumInput label="% Lead in Target" suffix="%" value={data.funnel.lead_target_pct} onChange={(v) => updateSection('funnel', 'lead_target_pct', v)} step={1} hint="Default: 90%" />
                    <NumInput label="% Da Clic a Visite Landing" suffix="%" value={data.funnel.click_to_visit_pct} onChange={(v) => updateSection('funnel', 'click_to_visit_pct', v)} step={1} hint="Default: 80%" />
                    <NumInput label="% Conversione Optin" suffix="%" value={data.funnel.optin_conversion_pct} onChange={(v) => updateSection('funnel', 'optin_conversion_pct', v)} step={1} hint="Default: 10%" />
                    <NumInput label="CTR (Click-Through Rate)" suffix="%" value={data.funnel.ctr_pct} onChange={(v) => updateSection('funnel', 'ctr_pct', v)} step={0.1} hint="Default: 1%" />
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
            {/* Funnel Output */}
            <Card className="bg-white border border-gray-100 shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm text-gray-500 uppercase tracking-wide">Output Funnel (Anno {selectedYear})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {[
                    { label: 'Impression', value: calc.impressions, bg: 'bg-slate-100', bar: 'bg-slate-300/60', text: 'text-slate-700' },
                    { label: 'Click', value: calc.clicks, bg: 'bg-violet-50', bar: 'bg-violet-200/60', text: 'text-violet-700' },
                    { label: 'Visite pagina', value: calc.pageVisits, bg: 'bg-purple-50', bar: 'bg-purple-200/60', text: 'text-purple-700' },
                    { label: 'Lead totali', value: calc.leadsTotal, bg: 'bg-sky-50', bar: 'bg-sky-200/60', text: 'text-sky-700' },
                    { label: 'Lead in target', value: calc.leadsTarget, bg: 'bg-blue-50', bar: 'bg-blue-200/70', text: 'text-blue-700' },
                    { label: 'App.ti fissati', value: calc.appointmentsScheduled, bg: 'bg-amber-50', bar: 'bg-amber-200/60', text: 'text-amber-700' },
                    { label: 'App.ti avvenuti', value: calc.appointmentsCompleted, bg: 'bg-orange-50', bar: 'bg-orange-200/70', text: 'text-orange-700' },
                    { label: 'Vendite', value: calc.salesNeeded, bg: 'bg-emerald-50', bar: 'bg-emerald-300/60', text: 'text-emerald-700' },
                  ].map((item, i) => {
                    const maxVal = Math.max(...[calc.impressions, calc.clicks, calc.pageVisits, calc.leadsTotal, calc.leadsTarget, calc.appointmentsScheduled, calc.appointmentsCompleted, calc.salesNeeded].filter(v => v > 0));
                    const logMax = Math.log10(Math.max(maxVal, 1) + 1);
                    const logVal = Math.log10(Math.max(item.value, 0) + 1);
                    const pct = logMax > 0 ? Math.max(6, (logVal / logMax) * 100) : 6;
                    return (
                      <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${item.bg} transition-all hover:shadow-sm`}>
                        <span className={`text-xs font-medium w-32 shrink-0 ${item.text}`}>{item.label}</span>
                        <div className="flex-1 h-5 bg-white/80 rounded-full overflow-hidden">
                          <div className={`h-full ${item.bar} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className={`text-sm font-bold tabular-nums w-20 text-right ${item.text}`}>{formatNumber(item.value)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 mt-4">
                  {[1, 2, 3].map(y => (
                    <Button key={y} size="sm" variant={selectedYear === y ? 'default' : 'outline'} onClick={() => setSelectedYear(y)}>Anno {y}</Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ MARKETING (CAC) ============ */}
        <TabsContent value="marketing">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5 text-orange-600" /> Marketing (CAC)</CardTitle>
              </CardHeader>
              <CardContent>
                <NumInput label="CAC Target (costo per cliente acquisito)" prefix="€" value={data.marketing.cac_target} onChange={(v) => updateSection('marketing', 'cac_target', v)} step={10} />
              </CardContent>
            </Card>
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-sm text-gray-500 uppercase tracking-wide">Output Marketing (Anno {selectedYear})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                  <span className="text-sm text-gray-600">Clienti da acquisire</span>
                  <span className="font-bold text-lg">{formatNumber(calc.salesNeeded)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-100 rounded-lg border border-blue-200">
                  <span className="text-sm font-medium text-blue-800">Budget Marketing Totale</span>
                  <span className="font-bold text-lg text-blue-800">{formatCurrency(calc.marketingBudget)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                  <span className="text-sm text-gray-600">CAC Budget</span>
                  <span className="font-semibold">{formatCurrency(calc.cac)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                  <span className="text-sm text-gray-600">Costo per lead target</span>
                  <span className="font-semibold">{formatCurrency(calc.costPerLeadTarget)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                  <span className="text-sm text-gray-600">Costo per appuntamento avvenuto</span>
                  <span className="font-semibold">{formatCurrency(calc.costPerApptCompleted)}</span>
                </div>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3].map(y => (
                    <Button key={y} size="sm" variant={selectedYear === y ? 'default' : 'outline'} onClick={() => setSelectedYear(y)}>Anno {y}</Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ TEAM COMMERCIALE ============ */}
        <TabsContent value="team">
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Setter */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700"><Users className="w-5 h-5" /> Setter</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <NumInput label="Appuntamenti fissati/avvenuti (per setter)" value={data.team.setter_appointments_per_month} onChange={(v) => updateSection('team', 'setter_appointments_per_month', v)} />
                  <NumInput label="Fee per appuntamento (opzionale)" prefix="€" value={data.team.setter_fee_per_appointment} onChange={(v) => updateSection('team', 'setter_fee_per_appointment', v)} step={5} />
                  <NumInput label="Stipendio fisso mensile (opzionale)" prefix="€" value={data.team.setter_fixed_salary} onChange={(v) => updateSection('team', 'setter_fixed_salary', v)} step={100} />
                </CardContent>
              </Card>
              {/* Venditori */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-700"><Users className="w-5 h-5" /> Venditori</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <NumInput label="Appuntamenti gestiti al mese (per venditore)" value={data.team.closer_appointments_per_month} onChange={(v) => updateSection('team', 'closer_appointments_per_month', v)} />
                  <p className="text-sm font-medium text-gray-500 pt-1">Provvigioni su vendita primaria</p>
                  <div className="grid grid-cols-2 gap-3">
                    <NumInput label="Commissione fissa per vendita" prefix="€" value={data.team.closer_commission_fixed} onChange={(v) => updateSection('team', 'closer_commission_fixed', v)} step={10} />
                    <NumInput label="Oppure % su vendita" suffix="%" value={data.team.closer_commission_pct} onChange={(v) => updateSection('team', 'closer_commission_pct', v)} step={1} />
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-sm font-medium text-gray-500">Provvigioni su upsell</p>
                    <Button variant="ghost" size="sm" className="text-xs text-blue-600 h-6 px-2" onClick={() => {
                      setData(prev => ({ ...prev, team: { ...prev.team, closer_upsell_commission_fixed: prev.team.closer_commission_fixed, closer_upsell_commission_pct: prev.team.closer_commission_pct } }));
                    }}>Copia da prima vendita</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <NumInput label="Commissione fissa per upsell" prefix="€" value={data.team.closer_upsell_commission_fixed} onChange={(v) => updateSection('team', 'closer_upsell_commission_fixed', v)} step={10} />
                    <NumInput label="Oppure % su upsell" suffix="%" value={data.team.closer_upsell_commission_pct} onChange={(v) => updateSection('team', 'closer_upsell_commission_pct', v)} step={1} />
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-sm font-medium text-gray-500">Provvigioni su recurring</p>
                    <Button variant="ghost" size="sm" className="text-xs text-blue-600 h-6 px-2" onClick={() => {
                      setData(prev => ({ ...prev, team: { ...prev.team, closer_recurring_commission_fixed: prev.team.closer_commission_fixed, closer_recurring_commission_pct: prev.team.closer_commission_pct } }));
                    }}>Copia da prima vendita</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <NumInput label="Commissione fissa per recurring" prefix="€" value={data.team.closer_recurring_commission_fixed} onChange={(v) => updateSection('team', 'closer_recurring_commission_fixed', v)} step={10} />
                    <NumInput label="Oppure % su recurring" suffix="%" value={data.team.closer_recurring_commission_pct} onChange={(v) => updateSection('team', 'closer_recurring_commission_pct', v)} step={1} />
                  </div>
                  <NumInput label="Stipendio fisso mensile (opzionale)" prefix="€" value={data.team.closer_fixed_salary} onChange={(v) => updateSection('team', 'closer_fixed_salary', v)} step={100} />
                </CardContent>
              </Card>
            </div>
            {/* Team Output */}
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-sm text-gray-500 uppercase tracking-wide">Output Team (Anno {selectedYear})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Setter output */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-blue-700">Setter</h4>
                    <div className="flex justify-between p-2 bg-white rounded border"><span className="text-sm">Setter necessari</span><span className="font-bold">{calc.numSetters}</span></div>
                    <div className="flex justify-between p-2 bg-white rounded border"><span className="text-sm">Appuntamenti/setter/mese</span><span className="font-semibold">{formatNumber(calc.apptsPerSetter)}</span></div>
                    <div className="flex justify-between p-2 bg-white rounded border">
                      <span className="text-sm">Guadagno medio/mese</span>
                      <span className={`font-bold ${calc.setterEarning < 2000 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(calc.setterEarning)}
                      </span>
                    </div>
                    <div className="flex justify-between p-2 bg-blue-50 rounded border border-blue-200"><span className="text-sm font-medium">Costo totale setter/mese</span><span className="font-bold">{formatCurrency(calc.totalSetterCostMo)}</span></div>
                  </div>
                  {/* Venditori output */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-purple-700">Venditori</h4>
                    <div className="flex justify-between p-2 bg-white rounded border"><span className="text-sm">Venditori necessari</span><span className="font-bold">{calc.numClosers}</span></div>
                    <div className="flex justify-between p-2 bg-white rounded border"><span className="text-sm">Appuntamenti/venditore/mese</span><span className="font-semibold">{formatNumber(calc.apptsPerCloser)}</span></div>
                    <div className="flex justify-between p-2 bg-white rounded border"><span className="text-sm">Vendite/venditore/mese</span><span className="font-semibold">{formatNumber(calc.salesPerCloser)}</span></div>
                    <div className="flex justify-between p-2 bg-white rounded border">
                      <span className="text-sm">Guadagno medio/mese</span>
                      <span className={`font-bold ${calc.closerEarning < 3000 ? 'text-red-600' : calc.closerEarning < 4500 ? 'text-amber-600' : 'text-green-600'}`}>
                        {formatCurrency(calc.closerEarning)}
                      </span>
                    </div>
                    <div className="flex justify-between p-2 bg-purple-50 rounded border border-purple-200"><span className="text-sm font-medium">Costo totale venditori/mese</span><span className="font-bold">{formatCurrency(calc.totalCloserCostMo)}</span></div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200 flex justify-between items-center">
                  <span className="font-medium text-amber-800">Costo Totale Team/mese</span>
                  <span className="font-bold text-lg text-amber-800">{formatCurrency(calc.totalTeamCostMo)}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  {[1, 2, 3].map(y => (
                    <Button key={y} size="sm" variant={selectedYear === y ? 'default' : 'outline'} onClick={() => setSelectedYear(y)}>Anno {y}</Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ COSTI ============ */}
        <TabsContent value="costi">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">
              {/* Fixed Agency Costs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-orange-600" /> Costi Fissi Agenzia</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {data.costs.fixed_costs.map((fc, i) => (
                    <div key={i} className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">{fc.name}</Label>
                        <CostAmountInput
                          value={fc.amount}
                          onChange={(val) => {
                            const newCosts = [...data.costs.fixed_costs];
                            newCosts[i] = { ...newCosts[i], amount: val };
                            setData(prev => ({ ...prev, costs: { ...prev.costs, fixed_costs: newCosts } }));
                          }}
                        />
                      </div>
                      <Badge variant="outline" className="mb-1 shrink-0">
                        {fc.is_percentage ? '% budget mkt' : fc.type === 'monthly' ? '/mese' : '/anno'}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
              {/* Custom Costs */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-gray-500" /> Costi Personalizzati</CardTitle>
                    <Button size="sm" variant="outline" onClick={() => {
                      setData(prev => ({
                        ...prev,
                        costs: { ...prev.costs, custom_costs: [...prev.costs.custom_costs, { name: '', amount: 0, type: 'monthly' }] }
                      }));
                    }}>
                      <Plus className="w-3 h-3 mr-1" /> Aggiungi
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {data.costs.custom_costs.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">Nessun costo personalizzato</p>
                  )}
                  {data.costs.custom_costs.map((cc, i) => (
                    <div key={i} className="flex items-end gap-2">
                      <div className="flex-1">
                        <Input
                          placeholder="Nome voce"
                          value={cc.name}
                          onChange={(e) => {
                            const newCosts = [...data.costs.custom_costs];
                            newCosts[i] = { ...newCosts[i], name: e.target.value };
                            setData(prev => ({ ...prev, costs: { ...prev.costs, custom_costs: newCosts } }));
                          }}
                        />
                      </div>
                      <div className="w-28">
                        <CostAmountInput
                          value={cc.amount}
                          onChange={(val) => {
                            const newCosts = [...data.costs.custom_costs];
                            newCosts[i] = { ...newCosts[i], amount: val };
                            setData(prev => ({ ...prev, costs: { ...prev.costs, custom_costs: newCosts } }));
                          }}
                          placeholder="€"
                        />
                      </div>
                      <Button
                        size="sm"
                        variant={cc.type === 'monthly' ? 'default' : 'outline'}
                        className="shrink-0 text-xs"
                        onClick={() => {
                          const newCosts = [...data.costs.custom_costs];
                          newCosts[i] = { ...newCosts[i], type: cc.type === 'monthly' ? 'annual' : 'monthly' };
                          setData(prev => ({ ...prev, costs: { ...prev.costs, custom_costs: newCosts } }));
                        }}
                      >
                        {cc.type === 'monthly' ? '/mese' : '/anno'}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 shrink-0" onClick={() => {
                        const newCosts = data.costs.custom_costs.filter((_, j) => j !== i);
                        setData(prev => ({ ...prev, costs: { ...prev.costs, custom_costs: newCosts } }));
                      }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
            {/* Costs Output */}
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-sm text-gray-500 uppercase tracking-wide">Riepilogo Costi (Anno {selectedYear})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-sm font-medium text-blue-800">Budget Marketing</span>
                  <span className="font-bold text-blue-800">{formatCurrency(calc.marketingBudget)}</span>
                </div>
                <div className="flex justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <span className="text-sm font-medium text-amber-800">Costo Team Sales</span>
                  <span className="font-bold text-amber-800">{formatCurrency(calc.totalTeamCostYr)}</span>
                </div>
                <div className="flex justify-between p-3 bg-white rounded-lg border">
                  <span className="text-sm">Costi Fissi</span>
                  <span className="font-semibold">{formatCurrency(calc.totalOtherCostsYr)}</span>
                </div>
                <div className="flex justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                  <span className="text-sm font-medium text-red-800">Costi Totali</span>
                  <span className="font-bold text-lg text-red-800">{formatCurrency(calc.totalCostsYr)}</span>
                </div>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3].map(y => (
                    <Button key={y} size="sm" variant={selectedYear === y ? 'default' : 'outline'} onClick={() => setSelectedYear(y)}>Anno {y}</Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ MASSIMIZZAZIONE ============ */}
        <TabsContent value="maximization">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-orange-600" /> Massimizzazione Database</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500">Recupero fatturato lavorando il database di lead non convertiti generati durante l'anno.</p>
                <NumInput label="% Appuntamenti fissati su database in 12 mesi" suffix="%" value={data.maximization?.db_appointment_rate || 0} onChange={(v) => updateSection('maximization', 'db_appointment_rate', v)} step={1} />
                <NumInput label="% Show-up" suffix="%" value={data.maximization?.db_show_up_rate || 50} onChange={(v) => updateSection('maximization', 'db_show_up_rate', v)} step={1} hint="Default: 50%" />
                <NumInput label="Fee per appuntamento Massimizzazione" prefix="€" value={data.maximization?.mx_fee_per_appointment ?? 100} onChange={(v) => updateSection('maximization', 'mx_fee_per_appointment', v)} step={5} hint="Default: 100€ — Fee riconosciuta al setter per ogni appuntamento da database" data-testid="mx-fee-input" />
                <div className="bg-gray-50 rounded-lg p-3 mt-2">
                  <p className="text-xs text-gray-400">La % di conversione in vendita usata e quella del funnel principale ({formatPct(data.funnel.conversion_rate)})</p>
                </div>
              </CardContent>
            </Card>
            {/* Output */}
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-sm text-gray-500 uppercase tracking-wide">Output Massimizzazione (Anno {selectedYear})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                  <span className="text-sm text-gray-600">Database disponibile (lead non convertiti)</span>
                  <span className="font-bold text-lg">{formatNumber(calc.mx.dbSize)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                  <span className="text-sm text-gray-600">Appuntamenti fissati</span>
                  <span className="font-semibold">{formatNumber(calc.mx.mxAppointments)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                  <span className="text-sm text-gray-600">Appuntamenti avvenuti (show-up)</span>
                  <span className="font-semibold">{formatNumber(calc.mx.mxShowUp)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-sm font-medium text-blue-800">Vendite da massimizzazione</span>
                  <span className="font-bold text-blue-800">{formatNumber(calc.mx.mxSales)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-orange-100 rounded-lg border border-orange-200">
                  <span className="text-sm font-medium text-orange-800">Fatturato recuperato</span>
                  <span className="font-bold text-lg text-orange-800">{formatCurrency(calc.mx.mxTotalRev)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg border border-green-200">
                  <span className="text-sm font-medium text-green-800">Margine lordo massimizzazione</span>
                  <span className="font-bold text-lg text-green-800">{formatCurrency(calc.mx.mxGrossMargin)}</span>
                </div>
                <div className="border-t pt-2 space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Costi Massimizzazione</p>
                  <div className="flex justify-between items-center p-2 bg-white rounded-lg border">
                    <span className="text-sm text-gray-600">Fee setter ({formatCurrency(calc.mx.mxFeePerAppt)}/appt × {formatNumber(calc.mx.mxAppointments)})</span>
                    <span className="font-semibold text-red-600">-{formatCurrency(calc.mx.mxSetterFeeCost)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded-lg border">
                    <span className="text-sm text-gray-600">Provvigioni venditori</span>
                    <span className="font-semibold text-red-600">-{formatCurrency(calc.mx.mxCloserCommCost)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded-lg border">
                    <span className="text-sm text-gray-600">Totale costi mx</span>
                    <span className="font-bold text-red-600">-{formatCurrency(calc.mx.mxTotalCost)}</span>
                  </div>
                </div>
                <div className={`flex justify-between items-center p-3 rounded-lg border ${calc.mx.mxNetProfit >= 0 ? 'bg-emerald-100 border-emerald-200' : 'bg-red-100 border-red-200'}`}>
                  <span className={`text-sm font-medium ${calc.mx.mxNetProfit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>Profitto netto massimizzazione</span>
                  <span className={`font-bold text-lg ${calc.mx.mxNetProfit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>{formatCurrency(calc.mx.mxNetProfit)}</span>
                </div>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3].map(y => (
                    <Button key={y} size="sm" variant={selectedYear === y ? 'default' : 'outline'} onClick={() => setSelectedYear(y)}>Anno {y}</Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ DASHBOARD ============ */}
        <TabsContent value="dashboard">
        <div className="bg-white">
          {/* Year & Time Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              {[1, 2, 3].map(y => (
                <Button key={y} variant={selectedYear === y ? 'default' : 'outline'} onClick={() => setSelectedYear(y)} data-testid={`year-btn-${y}`}>
                  Anno {y}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Annuale</span>
              <Switch checked={timeView === 'monthly'} onCheckedChange={(v) => setTimeView(v ? 'monthly' : 'annual')} data-testid="time-view-switch" />
              <span className="text-sm text-gray-500">Mensile</span>
            </div>
          </div>

          {/* KPI Cards */}
          <div className={`grid grid-cols-2 md:grid-cols-3 ${calc.hasMx ? 'lg:grid-cols-6' : 'lg:grid-cols-4'} gap-3 mb-5`}>
            <KPICard title={calc.hasMx ? "Fatt. Acquisizione" : "Fatturato"} value={formatCurrency(calc.d.totalRevenue)} icon={Target} color="orange" />
            {calc.hasMx && <KPICard title="Fatt. Massimizzazione" value={formatCurrency(calc.d.mxTotalRev)} icon={TrendingUp} color="purple" />}
            {calc.hasMx && <KPICard title="Fatturato Totale" value={formatCurrency(calc.d.grandTotalRevenue)} icon={BarChart3} color="blue" />}
            <KPICard title={calc.hasMx ? "Vendite Totali" : "Vendite"} value={formatNumber(calc.hasMx ? calc.d.salesNeeded + calc.mx.mxSales / (timeView === 'monthly' ? 12 : 1) : calc.d.salesNeeded)} subtitle={calc.hasMx ? `${formatNumber(calc.d.salesNeeded)} acq. + ${formatNumber(calc.mx.mxSales / (timeView === 'monthly' ? 12 : 1))} max.` : undefined} icon={Users} color="amber" />
            <KPICard title="Budget Mkt" value={formatCurrency(calc.d.marketingBudget)} icon={Megaphone} color="blue" />
            <KPICard title="Utile Netto" value={formatCurrency(calc.hasMx ? calc.d.grandNetProfit : calc.d.netProfit)} icon={CircleDollarSign} color={(calc.hasMx ? calc.grandNetProfit : calc.netProfit) >= 0 ? 'green' : 'red'} />
          </div>

          {/* Tables & Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* LEFT: Breakdown Funnel */}
            <Card className="border border-gray-100 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 rounded-full bg-indigo-500" />
                  <CardTitle className="text-base font-semibold text-gray-800">Breakdown Funnel</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const div = timeView === 'monthly' ? 12 : 1;
                  const funnelSteps = [
                    { label: 'Investimento Marketing', value: formatCurrency(calc.d.marketingBudget), isHighlight: true, color: 'bg-indigo-50 text-indigo-800 border border-indigo-200' },
                    { label: 'Impression', value: formatNumber(calc.impressions / div) },
                    { label: 'Click', value: formatNumber(calc.clicks / div) },
                    { label: 'Visite pagina', value: formatNumber(calc.pageVisits / div) },
                    { label: 'Lead totali', value: formatNumber(calc.leadsTotal / div) },
                    { label: 'Lead in target', value: formatNumber(calc.leadsTarget / div), isHighlight: true, color: 'bg-blue-50 text-blue-800 border border-blue-200' },
                    { label: 'Costo per lead target', value: formatCurrency(calc.costPerLeadTarget), isCost: true, costColor: 'text-blue-600' },
                    { label: 'App.ti fissati', value: formatNumber(calc.appointmentsScheduled / div), isHighlight: true, color: 'bg-amber-50 text-amber-800 border border-amber-200' },
                    { label: 'Costo per app.to fissato', value: formatCurrency(calc.costPerApptScheduled), isCost: true, costColor: 'text-amber-600' },
                    { label: 'App.ti avvenuti', value: formatNumber(calc.appointmentsCompleted / div), isHighlight: true, color: 'bg-orange-50 text-orange-800 border border-orange-200' },
                    { label: 'Costo per app.to avvenuto', value: formatCurrency(calc.costPerApptCompleted), isCost: true, costColor: 'text-orange-600' },
                    { label: 'Vendite', value: formatNumber(calc.d.salesNeeded), isHighlight: true, color: 'bg-emerald-50 text-emerald-800 border border-emerald-200' },
                  ];
                  return (
                    <div className="space-y-1 text-sm">
                      {funnelSteps.map((step) => (
                        <div key={step.label} className={`flex justify-between items-center px-3 py-1.5 rounded-md ${step.isHighlight ? step.color + ' font-semibold' : step.isCost ? 'pl-8 py-1' : 'text-gray-600'}`}>
                          <span className={step.isCost ? `text-xs font-medium ${step.costColor}` : ''}>{step.isCost ? '↳ ' + step.label : step.label}</span>
                          <span className={step.isHighlight ? 'text-base font-bold' : step.isCost ? `font-bold text-sm ${step.costColor}` : 'font-semibold'}>{step.value}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* RIGHT: Breakdown Ricavi */}
            <Card className="border border-gray-100 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 rounded-full bg-emerald-500" />
                  <CardTitle className="text-base font-semibold text-gray-800">Breakdown Ricavi</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between items-center px-3 py-2 rounded-md bg-gray-50 text-gray-800 font-semibold border border-gray-200 mb-1">
                    <span>Vendite</span><span className="text-base font-bold">{formatNumber(calc.d.salesNeeded)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Prima vendita</span><span className="font-semibold">{formatCurrency(calc.d.revenuePrimary)}</span></div>
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Referral</span><span className="font-semibold">{formatCurrency(calc.d.revenueReferral)}</span></div>
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Upsell</span><span className="font-semibold">{formatCurrency(calc.d.revenueUpsell)}</span></div>
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Recurring</span><span className="font-semibold">{formatCurrency(calc.d.revenueRecurring)}</span></div>
                  <div className="flex justify-between items-center px-3 py-2 rounded-md bg-blue-50 text-blue-800 font-bold text-base border-t border-blue-100 mt-2">
                    <span>Fatturato Totale</span><span>{formatCurrency(calc.d.totalRevenue)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1 text-red-500 text-xs mt-1"><span>Costi variabili vendita ({formatPct(100 - (data.revenue.margin_pct || 0))})</span><span className="font-medium">-{formatCurrency(calc.d.revenuePrimary + calc.d.revenueReferral - calc.d.grossMarginPrimary)}</span></div>
                  <div className="flex justify-between px-3 py-1 text-red-500 text-xs"><span>Costi variabili upsell ({formatPct(100 - (data.revenue.upsell_margin_pct || 0))})</span><span className="font-medium">-{formatCurrency(calc.d.revenueUpsell - calc.d.grossMarginUpsell)}</span></div>
                  <div className="flex justify-between px-3 py-1 text-red-500 text-xs"><span>Costi variabili recurring ({formatPct(100 - (data.revenue.recurring_margin_pct || 0))})</span><span className="font-medium">-{formatCurrency(calc.d.revenueRecurring - calc.d.grossMarginRecurring)}</span></div>
                  <div className="flex justify-between items-center px-3 py-2 rounded-md bg-emerald-50 text-emerald-800 font-bold text-base border-t border-emerald-100 mt-2">
                    <span>Margine Lordo</span><span>{formatCurrency(calc.d.grossMargin)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Breakdown Team */}
            <Card className="border border-gray-100 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 rounded-full bg-blue-500" />
                  <CardTitle className="text-base font-semibold text-gray-800">Breakdown Team</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Setter necessari</span><span className="font-semibold">{calc.numSetters}</span></div>
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Venditori necessari</span><span className="font-semibold">{calc.numClosers}</span></div>
                  <div className="flex justify-between items-center px-3 py-2 rounded-md bg-blue-50 text-blue-800 font-bold border border-blue-100 mt-1">
                    <span>Totale risorse</span><span className="text-base">{calc.numSetters + calc.numClosers}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1 mt-2"><span className="text-gray-600">Guadagno setter/mese</span>
                    <span className={`font-semibold ${calc.setterEarning < 2000 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(calc.setterEarning)}</span>
                  </div>
                  <div className="flex justify-between px-3 py-1"><span className="text-gray-600">Guadagno venditore/mese</span>
                    <span className={`font-semibold ${calc.closerEarning < 3000 ? 'text-red-600' : calc.closerEarning < 4500 ? 'text-amber-600' : 'text-green-600'}`}>{formatCurrency(calc.closerEarning)}</span>
                  </div>
                  <div className="flex justify-between items-center px-3 py-2 rounded-md bg-red-50 text-red-800 font-bold border border-red-100 mt-1">
                    <span>Costo Team</span><span className="text-base">{formatCurrency(calc.d.teamCost)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Breakdown Costi */}
            <Card className="border border-gray-100 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 rounded-full bg-red-500" />
                  <CardTitle className="text-base font-semibold text-gray-800">Breakdown Costi</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Marketing</span><span className="font-semibold">{formatCurrency(calc.d.marketingBudget)}</span></div>
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Team Sales</span><span className="font-semibold">{formatCurrency(calc.d.teamCost)}</span></div>
                  <div className="flex justify-between px-3 py-1 text-gray-600"><span>Costi Fissi</span><span className="font-semibold">{formatCurrency(calc.d.otherCosts)}</span></div>
                  <div className="flex justify-between items-center px-3 py-2 rounded-md bg-red-50 text-red-800 font-bold border border-red-100 mt-1">
                    <span>Totale Costi</span><span className="text-base">{formatCurrency(calc.d.totalCosts)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Massimizzazione Database (visibile solo se attiva) */}
            {calc.hasMx && (
            <Card className="border border-gray-100 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 rounded-full bg-orange-500" />
                  <CardTitle className="text-base font-semibold text-gray-800">Massimizzazione Database</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Database lead disponibile</span><span className="font-semibold">{formatNumber(calc.mx.dbSize)}</span></div>
                  <div className="flex justify-between"><span>Appuntamenti fissati</span><span className="font-semibold">{formatNumber(calc.mx.mxAppointments)}</span></div>
                  <div className="flex justify-between"><span>Appuntamenti avvenuti</span><span className="font-semibold">{formatNumber(calc.mx.mxShowUp)}</span></div>
                  <div className="flex justify-between"><span>Vendite da massimizzazione</span><span className="font-bold">{formatNumber(calc.mx.mxSales)}</span></div>
                  <div className="flex justify-between border-t pt-2"><span>Fatturato recuperato</span><span className="font-bold text-orange-600">{formatCurrency(calc.d.mxTotalRev)}</span></div>
                  <div className="flex justify-between font-bold text-green-700"><span>Margine lordo</span><span>{formatCurrency(calc.d.mxGrossMargin)}</span></div>
                  <div className="flex justify-between text-red-600"><span>Fee setter ({formatCurrency(calc.mx.mxFeePerAppt)}/appt)</span><span className="font-semibold">-{formatCurrency(calc.d.mxSetterFeeCost)}</span></div>
                  <div className="flex justify-between text-red-600"><span>Provvigioni venditori</span><span className="font-semibold">-{formatCurrency(calc.d.mxCloserCommCost)}</span></div>
                  <div className={`flex justify-between border-t pt-2 font-bold ${calc.mx.mxNetProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}><span>Profitto netto mx</span><span>{formatCurrency(calc.d.mxNetProfit)}</span></div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Chart: Crescita Fatturato 3 anni - FULL WIDTH */}
          </div>
          <Card className="border border-gray-100 shadow-sm mt-4">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-500" />
                <CardTitle className="text-base font-semibold text-gray-800">Crescita Fatturato e Utile</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={calc.chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} />
                  <YAxis tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px', color: '#f9fafb', fontSize: '12px' }} />
                  <Bar dataKey="nuovi" stackId="a" fill="#f97316" radius={[0, 0, 0, 0]} name="Nuovi Clienti" />
                  <Bar dataKey="recurring" stackId="a" fill="#6366f1" radius={[4, 4, 0, 0]} name="Recurring" />
                  <Line type="monotone" dataKey="utile" stroke="#10b981" strokeWidth={3} dot={{ r: 5, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }} name="Utile Netto" />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Profitto finale */}
          <Card className={`mt-4 border shadow-sm ${(calc.hasMx ? calc.grandNetProfit : calc.netProfit) >= 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
            <CardContent className="py-5">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Riepilogo Profitto ({timeView === 'monthly' ? 'Mensile' : 'Annuale'}) - Anno {selectedYear}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span>Margine Lordo{calc.hasMx ? ' Acq.' : ''}: <strong className="text-gray-700">{formatCurrency(calc.d.grossMargin)}</strong></span>
                    {calc.hasMx && <span>+ Margine Max.: <strong className="text-gray-700">{formatCurrency(calc.d.mxGrossMargin)}</strong></span>}
                    <span>- Marketing: <strong className="text-gray-700">{formatCurrency(calc.d.marketingBudget)}</strong></span>
                    <span>- Team{calc.hasMx ? ' (incl. mx)' : ''}: <strong className="text-gray-700">{formatCurrency(calc.d.teamCost)}</strong></span>
                    <span>- Costi Fissi: <strong className="text-gray-700">{formatCurrency(calc.d.otherCosts)}</strong></span>
                  </div>
                </div>
                <div className={`text-3xl font-bold ${(calc.hasMx ? calc.grandNetProfit : calc.netProfit) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  = {formatCurrency(calc.hasMx ? calc.d.grandNetProfit : calc.d.netProfit)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
